import { ReactNode } from 'react';
import { FieldCompletion } from './types';
import { MetamodelContent } from '@ardoq/api-types';
import { MetamodelContext } from 'metamodel/types';
import { CompletionBarHeader } from './completionBar/CompletionBarHeader';
import { CompletionBar } from './completionBar/CompletionBar';
import { dispatchAction } from '@ardoq/rxbeach';
import { setMetamodelContext } from 'metamodel/navigation/actions';
import { colors } from '@ardoq/design-tokens';
import { SimpleBlockDiagramContentType } from '@ardoq/simple-block-diagram/types';
import { DefaultNotification } from '@ardoq/status-ui';
import { Paragraph } from '@ardoq/typography';
import { Box } from '@ardoq/layout';
import { t, type I18nLocale } from '@ardoq/i18n';

type ComponentFieldCompletionWidgetProps = {
  componentTypeFieldCompletion?: FieldCompletion[];
  referenceTypeFieldCompletion?: FieldCompletion[];
  content: MetamodelContent;
  context: MetamodelContext;
  notification?: ReactNode;
  i18nLocale: I18nLocale;
};

const ComponentFieldCompletionWidget = ({
  componentTypeFieldCompletion,
  content,
  context,
  notification,
  i18nLocale,
}: ComponentFieldCompletionWidgetProps) => (
  <Box marginTop="xlarge">
    <Paragraph color="textModerate">
      {t(
        i18nLocale,
        'metamodel:stats.completion.componentTitle',
        { name: context.name },
        'Field completeness for component types in {{name}}'
      )}
    </Paragraph>
    {componentTypeFieldCompletion && componentTypeFieldCompletion.length ? (
      <Box marginTop="small">
        <CompletionBarHeader
          left={t(i18nLocale, 'metamodel:stats.completion.typeHeader', 'Type')}
          right={t(
            i18nLocale,
            'metamodel:stats.completion.valueHeader',
            '% of fields with value'
          )}
        />
        {componentTypeFieldCompletion.map(
          ({ id, name, completeness, filledFieldCount, totalFieldCount }) => {
            const textColor = totalFieldCount ? 'textDefault' : 'textSubtle';
            const fillColor = totalFieldCount
              ? colors.green500
              : colors.grey200;
            const label = totalFieldCount
              ? `${Math.floor(completeness)}%`
              : t(i18nLocale, 'metamodel:stats.completion.notAvailable', 'N/A');
            const hoverLabel = `${filledFieldCount}/${totalFieldCount}`;
            const componentType = content.componentTypes[id];
            return (
              <CompletionBar
                key={id}
                name={name}
                textColor={textColor}
                fillColor={fillColor}
                completeness={completeness}
                label={label}
                hoverLabel={hoverLabel}
                onClick={() => {
                  dispatchAction(
                    setMetamodelContext({
                      ...componentType,
                      type: SimpleBlockDiagramContentType.COMPONENT,
                    })
                  );
                }}
              />
            );
          }
        )}
      </Box>
    ) : (
      <DefaultNotification>{notification}</DefaultNotification>
    )}
  </Box>
);

export { ComponentFieldCompletionWidget };
